<script setup>
import { ref, computed, watch, provide, nextTick, useSlots, useTemplateRef, h, defineComponent, onBeforeUnmount, onMounted, reactive } from 'vue';
import { kebabCase } from 'lodash';
import { cn } from '/app/utils/helpers';
import { useResponsive } from '/app/composables/responsive';
import NTabsMenu from './TabsMenu.vue';
import NIcon from '/app/components/NIcon.vue';

const props = defineProps({
    modelValue: String,
    align: {
        type: String,
        default: 'between',
        validator: (value) => ['start', 'center', 'end', 'between'].includes(value),
    },
    outlined: {
        type: Boolean,
        default: false,
    },
    subheader: {
        type: Boolean,
        default: false,
    },
    mode: {
        type: String,
        default: 'full',
        validator: (value) => ['icons', 'full', 'menu'].includes(value),
    },
    activeClass: {
        type: String,
        default: 'is-active',
    },
    tabClass: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['change', 'update:modelValue']);
const slots = useSlots();

const responsive = useResponsive();
const rootEl = useTemplateRef('rootEl');

const tabs = ref([]);
const menu = ref(false);

const active = computed({
    get: () => kebabCase(props.modelValue),
    set: (value) => {
        emit('update:modelValue', value);
        emit('change', value);
    },
});

const asMenu = computed(() => responsive.sm && props.mode === 'menu');

const activeTabs = computed(() => {
    const filtered = tabs.value.filter((t) => !t.hidden);
    return asMenu.value ? filtered.filter((tab) => tab.isOpen) : filtered;
});

const inactiveTabs = computed(() => {
    const filtered = tabs.value.filter((t) => !t.hidden);
    return asMenu.value ? filtered.filter((tab) => !tab.isOpen) : [];
});

const hasActions = computed(() => !!slots.actions);

function prune() {
    if (!active.value && tabs.value.length) {
        const [tab] = tabs.value;
        selectTab(tab.computedId);
    }
}

function selectTab(id, menuStatus = false) {
    active.value = id;
    menu.value = menuStatus;
}

function scrollToActiveTab() {
    nextTick(() => {
        if (!responsive.sm) return;

        const el = rootEl.value?.querySelector('.is-active');
        if (el) {
            setTimeout(() => {
                el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                    inline: 'start',
                });
                el.focus();
            }, 300);
        }
    });
}

// Provide context for child Tab components
const tabsContext = reactive({
    tabs,
    get active() {
        return active.value;
    },
    set active(value) {
        active.value = value;
    },
});

provide('tabsContext', tabsContext);

watch(active, (newId) => {
    emit('change', newId);
    scrollToActiveTab();
}, { immediate: true });

watch(tabs, () => {
    nextTick(() => prune());
}, { deep: true });

onMounted(() => {
    prune();
});

onBeforeUnmount(() => {
    tabs.value = [];
});

const NTabButton = defineComponent({
    name: 'NTabButton',
    props: { target: Object },
    setup: (props, { slots, attrs }) => {
        return () => h('li', {
            ...attrs,
            class: [
                'whitespace-nowrap min-w-[60%] sm:min-w-[10px] snap-start',
                attrs.class,
                props.target.class,
                
            ],
        }, slots && slots.default());
    },
});
</script>

<template>
    <div ref="rootEl" class="tabs" :class="{ 'is-bordered': outlined }">
        <div
            class="tabs__header flex flex-row"
            :class="{
                'tabs__content-helper py-2 px-0': subheader,
                'items-end': asMenu,
                [`justify-${align}`]: !responsive.sm,
            }">
            <ul class="flex overflow-x-auto sb-tight sb-dust-200 snap-x w-full sm:w-auto">
                <NTabButton v-for="(tab, index) in activeTabs" :id="tab.computedId" :data-tab-id="tab.computedId" :target="tab" :key="index" :class="{'w-full sm:w-auto' :  asMenu }">
                    <a v-if="tab.disabled" :class="cn('!flex items-center justify-center py-4 px-4 sm:py-3 sm:px-4', tabClass, tab.class, tab.isOpen && activeClass, tab.disabled && 'is-disabled')">
                        <NIcon v-if="tab.icon" :as="tab.icon" left />
                        <span v-if="mode !== 'icons' || tab.isOpen">{{ tab.title }}</span>
                    </a>

                    <RouterLink v-else-if="tab.to" :to="tab.to" :class="cn('!flex items-center justify-center py-4 px-4 sm:py-3 sm:px-4', tabClass, tab.class, tab.isOpen && activeClass)" :active-class="activeClass">
                        <NIcon v-if="tab.icon" :as="tab.icon" left />
                        <span v-if="mode !== 'icons' || tab.isOpen">{{ tab.title }}</span>
                    </RouterLink>

                    <a v-else :href="`#${tab.computedId}`" :id="`tab-${tab.computedId}`" :class="cn('!flex items-center justify-center py-4 px-4 sm:py-3 sm:px-4', tabClass, tab.class, tab.isOpen && [activeClass, tab.activeClass])" @click.prevent="selectTab(tab.computedId)">
                        <NIcon v-if="tab.icon" :as="tab.icon" left />
                        <span v-if="mode !== 'icons' || tab.isOpen">{{ tab.title }}</span>
                    </a>
                </NTabButton>
            </ul>

            <NTabsMenu v-if="asMenu" v-model="menu" :width="50" class="ml-2 w-1/3 flex items-end justify-center">
                <template #activator="{ on }">
                    <button aria-label="button" v-on="on" class="btn has-no-style text-center">
                        <NIcon as="ellipsis-h-solid" class="font-size-1-2-rem" />
                    </button>
                </template>

                <div class="p-2 tab-menu min-w-60">
                    <ul>
                        <NTabButton v-for="(tab, index) in inactiveTabs" :target="tab" :data-tab-id="tab.computedId" :id="tab.computedId" :key="index" >
                            <a v-if="tab.disabled" :class="{ 'is-disabled': tab.disabled, [activeClass]: tab.isOpen }" class="flex items-center">
                                <slot :name="`header:${tab.computedId}`" v-bind="tab">
                                    <NIcon v-if="tab.icon" :as="tab.icon" left />{{ tab.title }}
                                </slot>
                            </a>

                            <router-link v-else-if="tab.to" :to="tab.to" class="flex items-center" :active-class="activeClass">
                                <slot :name="`header:${tab.computedId}`" v-bind="tab">
                                    <NIcon v-if="tab.icon" :as="tab.icon" left />{{ tab.title }}
                                </slot>
                            </router-link>

                            <a v-else :href="`#${tab.computedId}`" :class="{ [activeClass]: tab.isOpen }" class="flex items-center" @click.prevent="selectTab(tab.computedId)">
                                <slot :name="`header:${tab.computedId}`" v-bind="tab">
                                    <NIcon v-if="tab.icon" :as="tab.icon" left />{{ tab.title }}
                                </slot>
                            </a>
                        </NTabButton>
                    </ul>
                    <div v-if="hasActions" class="ml-3 mt-1">
                        <slot name="actions" />
                    </div>
                </div>
            </NTabsMenu>

            <div v-else-if="hasActions" class="ml-3">
                <slot name="actions" />
            </div>
        </div>
        <div class="tabs__content">
            <slot />
        </div>
    </div>
</template>